"use client";

import clsx from "clsx";
import { ChevronDown, ChevronUp, Plus, X } from "lucide-react";
import Image from "next/image";
import { useState } from "react";
import BackgroundVideo from "./BackgroundVideo";

export type InsideAcademyMedia =
  { type: "image"; src: string } | { type: "video"; src: string };

export type InsideAcademyItem = {
  title: string;
  text: string;
  media: InsideAcademyMedia;
};

type Props = {
  title: string;
  text: string;
  /** Shown while no item is open. */
  media: InsideAcademyMedia;
  items: InsideAcademyItem[];
};

// The frosted panels the items sit on, in brand blue rather than the design's grey.
const PANEL = "bg-brand-blue/85 backdrop-blur-[16px]";

/**
 * DAS Academies Inside a DAS Academy
 *
 * A full-bleed media panel with an accordion of features in its lower corner.
 * Opening a feature unfolds its text in place and swaps the background to its
 * own photo or video; closing it goes back to the section's own media. Only
 * one feature is open at a time.
 *
 * Every background is in the markup and cross-faded, so switching never waits
 * on a download; a video only loads once its feature is first opened.
 */
export default function InsideAcademy({ title, text, media, items }: Props) {
  const [open, setOpen] = useState<number | null>(null);

  const backgrounds = [media, ...items.map((item) => item.media)];
  const showing = open === null ? 0 : open + 1;

  const step = (by: number) => {
    const from = open ?? -1;

    setOpen((from + by + items.length) % items.length);
  };

  return (
    <section
      id="inside-a-das-academy"
      className="relative overflow-hidden min-h-[88svh] bg-brand-blue"
    >
      <div className="absolute inset-0">
        {backgrounds.map((background, index) => (
          <div
            key={index}
            className={clsx(
              "absolute inset-0 transition-opacity duration-700 ease-das",
              index === showing ? "opacity-100" : "opacity-0",
            )}
          >
            {background.type === "video" ? (
              <BackgroundVideo
                src={background.src}
                playing={index === showing}
              />
            ) : (
              <Image
                src={background.src}
                alt=""
                fill
                sizes="100vw"
                className="object-cover"
              />
            )}
          </div>
        ))}

        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(8,41,67,0.6)_0%,rgba(8,41,67,0)_35%,rgba(8,41,67,0)_55%,rgba(8,41,67,0.85)_100%)]"></div>
      </div>

      <div className="relative z-1 flex flex-col justify-between min-h-[88svh] py-14 mobile:py-10">
        <div className="shell">
          <div className="flex items-start justify-between gap-6 js-das-reveal">
            <div>
              <h2 className="mb-0! font-das text-[38px] leading-[1.08] font-bold tracking-[-0.02em] text-white mobile:text-[28px]">
                {title}
              </h2>
              <p className="max-w-[36ch] mt-3 mb-0 font-das text-[15px] leading-[1.55] text-das-green mobile:text-[14px]">
                {text}
              </p>
            </div>

            {open !== null && (
              <button
                type="button"
                className={clsx(
                  "flex shrink-0 items-center gap-2 px-3.5 py-2 border border-white/30 rounded-full font-das text-[12px] font-semibold text-white shadow-lg transition-colors hover:bg-white/10",
                  PANEL,
                )}
                aria-label="Close selected Academy feature"
                onClick={() => setOpen(null)}
              >
                <X aria-hidden="true" className="size-[15px]" />
                Close
              </button>
            )}
          </div>
        </div>

        <div className="shell mt-10">
          <div className="flex items-end gap-3">
            {open !== null && (
              <div className="flex flex-col gap-1 pb-0.5 mobile:hidden">
                <StepButton label="Previous" onClick={() => step(-1)}>
                  <ChevronUp aria-hidden="true" className="size-[15px]" />
                </StepButton>
                <StepButton label="Next" onClick={() => step(1)}>
                  <ChevronDown aria-hidden="true" className="size-[15px]" />
                </StepButton>
              </div>
            )}

            <ul className="flex flex-col items-start gap-2 m-0 p-0 list-none mobile:w-full">
              {items.map((item, index) => (
                <li key={item.title} className="m-0 mobile:w-full">
                  {open === index ? (
                    <div
                      className={clsx(
                        "max-w-[320px] rounded-[20px] px-6 py-5 mobile:max-w-none",
                        PANEL,
                      )}
                    >
                      <p className="mb-0 font-das text-[15px] leading-[1.65] text-white mobile:text-[14px]">
                        <span className="font-bold">{item.title}.</span>{" "}
                        <span className="text-white/80">{item.text}</span>
                      </p>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className={clsx(
                        "flex items-center gap-3 py-3 pr-5 pl-3 rounded-full text-left transition-[filter] hover:brightness-125",
                        PANEL,
                      )}
                      aria-expanded={false}
                      onClick={() => setOpen(index)}
                    >
                      <span className="flex size-8 shrink-0 items-center justify-center border border-white/40 rounded-full text-white">
                        <Plus aria-hidden="true" className="size-4" />
                      </span>
                      <span className="font-das text-[15px] font-semibold text-white mobile:text-[14px]">
                        {item.title}
                      </span>
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function StepButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      className="flex size-8 items-center justify-center border border-white/30 rounded-full text-white transition-opacity hover:opacity-70"
      onClick={onClick}
    >
      {children}
    </button>
  );
}
